import { AppBar, useMediaQuery, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { shallowEqual } from 'react-redux';

import { CLIENT_DETAILS_PAGE, CRM_CLIENTS_PAGE } from 'src/constants';
import {
  BANNER_HEIGHT,
  COMPANY_SWITCHER_WIDTH,
  DESKTOP_APP_BAR_HEIGHT,
  getSidebarDefaultPx,
  getSidebarMinPx,
  MOBILE_APP_BAR_HEIGHT,
  SIDEBAR_MAX_PX,
} from 'src/constants/uiConsts';
import { RouteContext, useFeatureFlags } from 'src/context';
import { useCompanySwitcherVisibility } from 'src/features/companies/useCompanySwitcherVisibility';
import { ContextBarLayout } from 'src/features/context-bar/ContextBarLayout';
import { ReferralPromoModal } from 'src/features/referrals/sidebar-promo/ReferralPromoModal';
import { ResizableGroup } from 'src/features/resizableWrapper/ResizableGroup';
import { ResizablePanel } from 'src/features/resizableWrapper/ResizablePanel';
import { ResizeHandle } from 'src/features/resizableWrapper/ResizeHandle';
import { WhatsNew } from 'src/features/whats-new/WhatsNew';
import { useBannerOptions } from 'src/hooks/useBannerOptions';
import { useElectronSidebar } from 'src/hooks/useElectronSidebar';
import { useImportProgressNotifier } from 'src/hooks/useImportProgressNotifier';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useWindowSize } from 'src/hooks/useWindowResize';
import AuthModal from 'src/legacy/components/Auth/AuthModal';
import { Banner } from 'src/legacy/components/Banner';
import { CommandBar } from 'src/legacy/components/CommandBar';
import { GeneralCommandbarActionsInitializer } from 'src/legacy/components/CommandBar/GeneralCommandbarActionsInitializer';
import { CompleteExternalActionModal } from 'src/legacy/components/Modals';
import Navbar from 'src/legacy/components/Navbar';
import { ShortcutsSidebar } from 'src/legacy/components/ShortcutsDrawer';
import Sidebar from 'src/legacy/components/Sidebar';
import { RootState } from 'src/store/reduxTypes';
import { updateSearchValueAction } from 'src/store/ui/actions';
import * as Colors from 'src/theme/colors';

import { globalCss } from '../globalCss';
import { HelpCenterModal } from '../HelpCenterModal';

import { HeaderSlotProvider, useHeaderSlot } from './HeaderSlotContext';
import { ShellProvider, useShell } from './ShellContext';

const SIDEBAR_PANEL_IDS = ['primary-sidebar-panel', 'primary-content-panel'];

const PAGES_WITH_RETAINED_SEARCH = [
  CRM_CLIENTS_PAGE.path,
  CLIENT_DETAILS_PAGE.path,
];

/**
 * Inner component that consumes ShellContext and HeaderSlotContext.
 * Separated so the providers are established before any consumer hooks run.
 */
const AppShellInner: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    drawerWidth,
    sidebarOpen,
    openMobileSidebar,
    closeMobileSidebar,
    showContextBar,
    contextBarContentMinWidth,
    isResizableSidebarActive, // this defines whether the sidebar is resizable -> combiles checking whether the flag is enable and the screen is desktop
    sidebarPanelRef,
    onSidebarPanelResize,
    onSidebarCollapse,
    onSidebarExpand,
    portalPageContent,
  } = useShell();
  const { headerSlot, ignoreHeaderPadding, showSidebar } = useHeaderSlot();
  const { pathname } = useContext(RouteContext);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const { width: viewportWidth } = useWindowSize();

  // Sync sidebar state with the Electron chrome bar toggle button
  useElectronSidebar();

  const { EnableStudioSidebar } = useFeatureFlags();
  const companySwitcherVisibility = useCompanySwitcherVisibility();
  const companySwitcherOffset =
    companySwitcherVisibility.visibility === 'desktop'
      ? COMPANY_SWITCHER_WIDTH
      : 0;

  const sidebarMinPx =
    getSidebarMinPx(EnableStudioSidebar) + companySwitcherOffset;
  const sidebarDefaultPx =
    getSidebarDefaultPx(EnableStudioSidebar) + companySwitcherOffset;
  const sidebarMaxPx = SIDEBAR_MAX_PX + companySwitcherOffset;

  // Breakpoints matching the original Layout's responsive behavior:
  // down('xs') = max-width 599.95px (mobile navbar height)
  // up('md') = min-width 960px (desktop drawer padding)
  const isXsDown = useMediaQuery(theme.breakpoints.down('xs'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const {
    loaded: userLoaded,
    loading: userLoading,
    isClient,
  } = useAppSelector(
    (state: RootState) => ({
      loaded: state.user.loaded,
      loading: state.user.loading,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );

  const includeContextBar = showContextBar && !isClient;
  const bannerOptions = useBannerOptions();

  const showBanner = Boolean(bannerOptions);

  // headerSlot === undefined → no page has set a header (show default navbar)
  // headerSlot === null → page explicitly hides navbar (fullbleed)
  // headerSlot === <ReactNode> → custom header content
  const hasNavbar = headerSlot !== null;

  const navbarHeight = isXsDown
    ? MOBILE_APP_BAR_HEIGHT
    : includeContextBar
      ? 60
      : DESKTOP_APP_BAR_HEIGHT;
  const appBarHeight =
    (hasNavbar ? navbarHeight : 0) + (showBanner ? BANNER_HEIGHT : 0);
  // In the resizable layout the banner renders as its own full-width row above
  // the sidebar, so the in-panel app bar only accounts for the navbar.
  const resizableAppBarHeight = hasNavbar ? navbarHeight : 0;

  const appBarStyle: React.CSSProperties = {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    boxShadow: 'none',
    zIndex: theme.zIndex.drawer + 1,
    borderBottom: hasNavbar
      ? `1px solid ${Colors.DividersAndCardBorders}`
      : 'none',
    height: appBarHeight,
    paddingLeft: isMdUp && showSidebar ? drawerWidth : 0,
  };
  if (!includeContextBar) {
    appBarStyle.left = '50%';
    appBarStyle.transform = 'translateX(-50%)';
  }

  const resizableAppBarStyle: React.CSSProperties = {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    boxShadow: 'none',
    borderBottom: hasNavbar
      ? `1px solid ${Colors.DividersAndCardBorders}`
      : 'none',
    height: resizableAppBarHeight,
  };

  const useResizableShell =
    isResizableSidebarActive && showSidebar && viewportWidth > 0;

  // Header slot cleanup is handled by PageShell and LayoutShim unmount effects.
  // An AppShell-level clearHeader on pathname change would race against child
  // mount effects (React fires child effects before parent effects), overriding
  // the new page's setHeader call.

  // Search bar should persist query between client list and client details.
  // Reset when navigating away from those pages.
  React.useEffect(() => {
    if (!PAGES_WITH_RETAINED_SEARCH.some((page) => pathname.startsWith(page))) {
      dispatch(updateSearchValueAction(''));
    }
    return () => {
      if (
        !PAGES_WITH_RETAINED_SEARCH.some((page) => pathname.startsWith(page))
      ) {
        dispatch(updateSearchValueAction(''));
      }
    };
  }, [pathname, dispatch]);

  // Reset scroll position when navigating between pages.
  // In non-panel mode, rootRef's div is the scroll container (overflowY: auto).
  // In panel mode, the content wrapper div (contentRef) is the scroll container.
  React.useEffect(() => {
    const target = useResizableShell ? contentRef.current : rootRef.current;
    if (target) {
      target.scrollTop = 0;
    }
  }, [pathname, useResizableShell]);

  useImportProgressNotifier();

  const showPrimarySidebarMobile = React.useCallback(() => {
    openMobileSidebar();
  }, [openMobileSidebar]);

  const hidePrimarySidebarMobile = React.useCallback(() => {
    closeMobileSidebar();
  }, [closeMobileSidebar]);

  const panelNavbar = hasNavbar && (
    <Navbar
      userLoaded={userLoaded}
      isClient={isClient}
      userLoading={userLoading}
      onOpenSidebarMobile={showPrimarySidebarMobile}
      showSearchbar={false}
      showNavbarAuthActions
      headerContent={headerSlot || undefined}
      ignoreHeaderPadding={ignoreHeaderPadding}
    />
  );

  // Portaling page content preserves React state when the shell swaps between
  // resizable and legacy layouts at the md (960px) breakpoint (POR-21388).
  // Pages opt in via PageShell `portalPageContent` until this rolls out broadly.
  //
  // React remounts a portal's children whenever the container element changes
  // identity, so portaling into the host each branch renders would still throw
  // the page away on the swap — the whole point of portaling. The container is
  // therefore one detached node owned by the shell; the branch that is mounted
  // adopts it, and the page inside it is moved rather than rebuilt.
  const pagePortalNodeRef = React.useRef<HTMLDivElement | null>(null);
  if (pagePortalNodeRef.current === null && typeof document !== 'undefined') {
    const node = document.createElement('div');
    // No box of its own: the anchor below owns the layout, so the page's own
    // children stay direct flex items of it exactly as they were inline.
    node.style.display = 'contents';
    pagePortalNodeRef.current = node;
  }
  const pagePortalNode = pagePortalNodeRef.current;

  // Rendered only once the node is in the document, so page content still
  // mounts against a laid-out ancestor and first-paint measurements hold.
  const [isPagePortalNodeAttached, setIsPagePortalNodeAttached] =
    React.useState(false);

  const attachPagePortalNode = React.useCallback(
    (anchor: HTMLDivElement | null) => {
      // Ignore the detach: on the swap the outgoing anchor unmounts before the
      // incoming one attaches, and dropping the node in between would remount
      // the page for exactly the reason the portal exists to avoid.
      if (!anchor || !pagePortalNode) return;
      if (pagePortalNode.parentNode !== anchor) {
        anchor.appendChild(pagePortalNode);
      }
      setIsPagePortalNodeAttached(true);
    },
    [pagePortalNode],
  );

  const pagePortalHost = (
    <div
      ref={attachPagePortalNode}
      className="flex w-full min-h-0 flex-1 flex-col"
      data-testid="appshell-page-portal-host"
    />
  );

  const pageContentSlot = portalPageContent ? pagePortalHost : children;

  const shellContentWithoutResizableSidebar = (
    <>
      <AppBar
        position={includeContextBar ? 'sticky' : 'fixed'}
        style={appBarStyle}
      >
        {bannerOptions ? <Banner {...bannerOptions} /> : null}
        {panelNavbar}
      </AppBar>
      {showSidebar && (
        <Sidebar
          onMobileClose={hidePrimarySidebarMobile}
          openMobile={sidebarOpen}
          resizeable={false}
        />
      )}
      {pageContentSlot}
    </>
  );

  const shellContentWithResizableSidebar = (
    <div className="flex flex-col h-full w-full">
      {bannerOptions ? <Banner {...bannerOptions} /> : null}
      <ResizableGroup
        widthPersistKey="primary-sidebar"
        persistPanelIds={SIDEBAR_PANEL_IDS}
        className="flex-1 min-h-0 w-full flex"
      >
        <ResizablePanel
          id="primary-sidebar-panel"
          className="z-1"
          collapsible
          collapsedSize={0}
          minSize={sidebarMinPx}
          defaultSize={sidebarDefaultPx}
          maxSize={sidebarMaxPx}
          groupResizeBehavior="preserve-pixel-size"
          ref={sidebarPanelRef}
          onResize={onSidebarPanelResize}
          onCollapse={onSidebarCollapse}
          onExpand={onSidebarExpand}
        >
          {showSidebar && (
            <Sidebar
              onMobileClose={hidePrimarySidebarMobile}
              openMobile={sidebarOpen}
              resizeable
            />
          )}
        </ResizablePanel>
        <ResizeHandle className="z-1" />
        <ResizablePanel
          id="primary-content-panel"
          className="flex flex-col min-h-0 overflow-hidden"
        >
          {!includeContextBar && (
            <AppBar position="sticky" style={resizableAppBarStyle}>
              {panelNavbar}
            </AppBar>
          )}
          <div
            ref={contentRef}
            className="overflow-y-auto flex-1 min-h-0 flex flex-col"
          >
            {includeContextBar ? (
              <ContextBarLayout contentMinWidth={contextBarContentMinWidth}>
                <AppBar position="sticky" style={resizableAppBarStyle}>
                  {panelNavbar}
                </AppBar>
                {pageContentSlot}
              </ContextBarLayout>
            ) : (
              pageContentSlot
            )}
          </div>
        </ResizablePanel>
      </ResizableGroup>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className="flex h-dvh box-border"
      style={{
        fontFamily: theme.typography.fontFamily,
        ...(useResizableShell
          ? {}
          : {
              paddingTop: includeContextBar ? undefined : appBarHeight,
              overflowY: includeContextBar
                ? undefined
                : isXsDown
                  ? 'hidden'
                  : 'auto',
            }),
      }}
    >
      <CommandBar>
        {useResizableShell ? (
          shellContentWithResizableSidebar
        ) : includeContextBar ? (
          <ContextBarLayout contentMinWidth={contextBarContentMinWidth}>
            {shellContentWithoutResizableSidebar}
          </ContextBarLayout>
        ) : (
          shellContentWithoutResizableSidebar
        )}
        {portalPageContent &&
          pagePortalNode &&
          isPagePortalNodeAttached &&
          createPortal(children, pagePortalNode)}
        <HelpCenterModal />
        <AuthModal />
        {userLoaded && (
          <>
            <CompleteExternalActionModal />
            <ReferralPromoModal />
            <GeneralCommandbarActionsInitializer />
            <WhatsNew />
          </>
        )}
        <ShortcutsSidebar />
      </CommandBar>
    </div>
  );
};

export interface AppShellProps {
  children: React.ReactNode;
}

const APPSHELL_RESET_ID = 'appshell-reset';

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  // Inject critical CSS resets that the design-system normally provides via
  // an inline <style> tag in production SSR. On local dev the design-system
  // rules live inside a @layer and don't override browser defaults reliably.
  React.useLayoutEffect(() => {
    if (document.getElementById(APPSHELL_RESET_ID)) return;
    const style = document.createElement('style');
    style.id = APPSHELL_RESET_ID;
    style.textContent = globalCss.join('');
    document.head.appendChild(style);
  }, []);

  return (
    <ShellProvider>
      <HeaderSlotProvider>
        <AppShellInner>{children}</AppShellInner>
      </HeaderSlotProvider>
    </ShellProvider>
  );
};

export default AppShell;
